import * as React from 'react';

import {
  enumBookingSortOption,
  enumBookingStatus,
} from '@rematter/frontend-lib-api-client';
import { cn, MultiSelect } from '@rematter/ui-web';
import { atom, useAtomValue } from 'jotai';
import uniqBy from 'lodash/uniqBy';

import { bookingsListQueryAtom } from '../../_graphql/queryBookingsList';
import { useTranslations } from '../../i18n/useTranslations';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

export type BookingOption = {
  id: string;
  bookingNumber: string;
};

export type BookingSelectProps = {
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
  onSelectionChange?: (entries: BookingOption[]) => void;
  selectedEntries?: BookingOption[];
  className?: string;
  isDisabled?: boolean;
};

export const BookingMultiSelect = ({
  isRequired = false,
  label,
  placeholder,
  selectedEntries = [],
  onSelectionChange,
  className,
  isDisabled = false,
}: BookingSelectProps) => {
  const t = useTranslations();
  const resolvedLabel = label ?? t.shared.bookingMultiSelect.label;
  const resolvedPlaceholder =
    placeholder ?? t.shared.bookingMultiSelect.placeholder;
  const bookingFieldRef = React.useRef<HTMLDivElement>(null);

  const [$bookingSearchTerm] = React.useState(() => atom(''));

  const [$bookings] = React.useState(() =>
    bookingsListQueryAtom((get) => {
      const searchTerm = get($bookingSearchTerm);
      return {
        variables: {
          searchTerm: searchTerm,
          limit: 6,
          offset: 0,
          sorting: [enumBookingSortOption.CreatedAtDesc],
          status: searchTerm ? [] : [enumBookingStatus.Open],
        },

        skip: false,
      };
    }),
  );

  const { data } = useAtomValue($bookings);

  const bookingsOptions = React.useMemo(
    () =>
      uniqBy([...(data?.bookings ?? []), ...selectedEntries], 'id').map(
        (entry) => ({
          key: entry.id,
          label: entry.bookingNumber,
        }),
      ),
    [data?.bookings, selectedEntries],
  );

  const [_searchTerm, setSearchTerm] = useDebouncedAtom(
    $bookingSearchTerm,
    300,
  );

  const handleSelectionChange = React.useCallback(
    (keys: string[]) => {
      onSelectionChange?.(
        bookingsOptions
          .filter((entry) => keys.includes(entry.key))
          .map((entry) => ({
            id: entry.key,
            bookingNumber: entry.label,
          })),
      );
    },
    [onSelectionChange, bookingsOptions],
  );

  React.useEffect(
    () => () => {
      setSearchTerm('');
    },
    [setSearchTerm],
  );

  return (
    <MultiSelect
      ref={bookingFieldRef}
      label={resolvedLabel}
      placeholder={resolvedPlaceholder}
      options={bookingsOptions}
      onSelectionChange={handleSelectionChange}
      selectedKeys={selectedEntries.map((entry) => entry.id)}
      className={cn('@BookingMultiSelect', className)}
      onSearch={(query) => {
        setSearchTerm(query);
      }}
      required={isRequired}
      isDisabled={isDisabled}
    />
  );
};
